import React from 'react';
import { useContent } from '../../hooks/useContent';
import styles from './AboutUs.module.css';

const AboutUs = () => {
  const { t } = useContent();
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  // Helper for image paths
  const getImagePath = (key, fallback) => {
    const path = t(key, fallback);
    if (path && path.startsWith('/uploads')) {
      return `${API_URL.replace('/api', '')}${path}`;
    }
    return path || fallback;
  };
  return (
    <div className={styles.container}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroContent}>
          <div className={styles.heroText}>
            <h1 className={styles.heroTitle}>{t('about.hero.title', 'About us')}</h1>
            <p className={styles.heroDescription}>
              {t('about.hero.description', 'The Advances in Applied Engineering Sciences (AdvAES) journal is the result of a productive collaboration between two leading higher education institutions: Anhalt University of Applied Sciences (Hochschule Anhalt, HSA) in Germany and Tashkent University of Applied Sciences in Uzbekistan. This partnership serves as a prime example of successful international scientific cooperation aimed at advancing applied engineering sciences.')}
            </p>
            <a href={t('about.hero.link', 'https://www.hs-anhalt.de/en/international/studyexchange-and-internship-abroad/partner-universities/exchange/tashkent-university-for-applied-sciences.html')}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.readMoreBtn}>
              {t('about.hero.linkText', 'Read more')}
            </a>
          </div>
          <div className={styles.heroImage}>
            <img
              src={getImagePath('about.hero.image', '/Images/about-us.jpg')}
              alt={t('about.hero.image.alt', 'Team collaboration')}
            />
          </div>
        </div>
      </section>

      {/* Key Aspects Section */}
      <section className={styles.keyAspectsSection}>
        <h2 className={styles.sectionTitle}>{t('about.aspects.title', 'Key Aspects of the Partnership')}</h2>

        <div className={styles.aspectsGrid}>
          <div className={styles.aspectCard}>
            <div className={styles.aspectIcon}>🤝</div>
            <h3 className={styles.aspectTitle}>{t('about.aspects.joint.title', 'Joint Initiative')}</h3>
            <p className={styles.aspectText}>
              {t('about.aspects.joint.description', 'The idea for the journal emerged from shared scientific interests and a commitment to knowledge exchange between the two universities.')}
            </p>
          </div>

          <div className={styles.aspectCard}>
            <div className={styles.aspectIcon}>🌍</div>
            <h3 className={styles.aspectTitle}>{t('about.aspects.editorial.title', 'International Editorial Board')}</h3>
            <p className={styles.aspectText}>
              {t('about.aspects.editorial.description', 'The journal\'s editorial board includes leading scientists and experts from both universities, as well as from other countries, ensuring a high scientific standard for publications.')}
            </p>
          </div>

          <div className={styles.aspectCard}>
            <div className={styles.aspectIcon}>💡</div>
            <h3 className={styles.aspectTitle}>{t('about.aspects.knowledge.title', 'Knowledge and Resource Exchange')}</h3>
            <p className={styles.aspectText}>
              {t('about.aspects.knowledge.description', 'This collaboration facilitates the exchange of expertise in research, publishing, and editorial practices.')}
            </p>
          </div>

          <div className={styles.aspectCard}>
            <div className={styles.aspectIcon}>📚</div>
            <h3 className={styles.aspectTitle}>{t('about.aspects.reach.title', 'Expanded Publication Reach')}</h3>
            <p className={styles.aspectText}>
              {t('about.aspects.reach.description', 'The journal provides researchers from different countries the opportunity to publish their work in a respected international journal.')}
            </p>
          </div>

          <div className={styles.aspectCard}>
            <div className={styles.aspectIcon}>🔗</div>
            <h3 className={styles.aspectTitle}>{t('about.aspects.global.title', 'Global Scientific Collaboration')}</h3>
            <p className={styles.aspectText}>
              {t('about.aspects.global.description', 'The journal strengthens academic ties between Germany and Uzbekistan while fostering connections with researchers worldwide.')}
            </p>
          </div>

          <div className={styles.aspectCard}>
            <div className={styles.aspectIcon}>📝</div>
            <h3 className={styles.aspectTitle}>{t('about.aspects.platform.title', 'Publication Platform')}</h3>
            <p className={styles.aspectText}>
              {t('about.aspects.platform.description', 'Faculty members from both universities can publish their scientific findings in the journal.')}
            </p>
          </div>
        </div>
      </section>

      {/* Partner Universities Section */}
      <section className={styles.universitiesSection}>
        <h2 className={styles.sectionTitle}>{t('about.partner.title', 'Partner Universities')}</h2>

        <div className={styles.universityCard}>
          <div className={styles.universityHeader}>
            <div className={styles.universityLogo}>
              <img src={getImagePath('about.partner.hsa.logo', 'Images/HochschuleAnhalt.jpg')} alt="Hochschule Anhalt" />
            </div>
            <div>
              <h3 className={styles.universityName}>{t('about.partner.hsa.name', 'Hochschule Anhalt')}</h3>
              <p className={styles.universitySubtitle}>{t('about.partner.hsa.subtitle', 'Anhalt University of Applied Sciences, Germany')}</p>
            </div>
          </div>
          <div className={styles.universityContent}>
            <div className={styles.universityStats}>
              <div className={styles.stat}>
                <span className={styles.statLabel}>{t('about.partner.foundation', 'Foundation:')}</span>
                <span className={styles.statValue}>{t('about.partner.hsa.foundation', '1891')}</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>{t('about.partner.students', 'Students:')}</span>
                <span className={styles.statValue}>{t('about.partner.hsa.students', '~7,500')}</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>{t('about.partner.intStudents', 'International Students:')}</span>
                <span className={styles.statValue}>{t('about.partner.hsa.intStudents', '2,500')}</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>{t('about.partner.faculty', 'Faculty & Staff:')}</span>
                <span className={styles.statValue}>{t('about.partner.hsa.faculty', '~700')}</span>
              </div>
            </div>

            <div className={styles.universityDetails}>
              <h4 className={styles.detailsTitle}>{t('about.partner.facultiesLabel', 'Faculties:')}</h4>
              <ul className={styles.detailsList}>
                <li>{t('about.partner.hsa.faculty1', 'Architecture, Civil Engineering, and Geoinformation')}</li>
                <li>{t('about.partner.hsa.faculty2', 'Design')}</li>
                <li>{t('about.partner.hsa.faculty3', 'Economics')}</li>
                <li>{t('about.partner.hsa.faculty4', 'Agriculture, Nutritional Science, and Landscape Architecture')}</li>
                <li>{t('about.partner.hsa.faculty5', 'Electrical Engineering, Mechanical Engineering, and Industrial Engineering')}</li>
                <li>{t('about.partner.hsa.faculty6', 'Computer Science and Languages')}</li>
                <li>{t('about.partner.hsa.faculty7', 'Applied Biosciences and Process Engineering')}</li>
              </ul>
            </div>
          </div>
          <a href={t('about.partner.hsa.link', 'https://anhalt.university')}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.universityBtn}>
            {t('about.partner.moreDetails', 'More details')}
          </a>
        </div>

        <div className={styles.universityCard}>
          <div className={styles.universityHeader}>
            <div className={styles.universityLogo}>
              <img src={getImagePath('about.partner.utas.logo', '/Images/utas-1.jpg')} alt="UTAS" />
            </div>
            <div>
              <h3 className={styles.universityName}>{t('about.partner.utas.name', 'UTAS')}</h3>
              <p className={styles.universitySubtitle}>{t('about.partner.utas.subtitle', 'Tashkent University of Applied Sciences, Uzbekistan')}</p>
            </div>
          </div>
          <div className={styles.universityContent}>
            <div className={styles.universityStats}>
              <div className={styles.stat}>
                <span className={styles.statLabel}>{t('about.partner.foundation', 'Foundation:')}</span>
                <span className={styles.statValue}>{t('about.partner.utas.foundation', '2020')}</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>{t('about.partner.students', 'Students:')}</span>
                <span className={styles.statValue}>{t('about.partner.utas.students', '~5,000')}</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>{t('about.partner.faculty', 'Faculty & Staff:')}</span>
                <span className={styles.statValue}>{t('about.partner.utas.faculty', '~300')}</span>
              </div>
            </div>

            <div className={styles.universityDetails}>
              <h4 className={styles.detailsTitle}>{t('about.partner.facultiesLabel', 'Faculties:')}</h4>
              <ul className={styles.detailsList}>
                <li>{t('about.partner.utas.faculty1', 'Information Technology')}</li>
                <li>{t('about.partner.utas.faculty2', 'Engineering')}</li>
                <li>{t('about.partner.utas.faculty3', 'Economics')}</li>
                <li>{t('about.partner.utas.faculty4', 'Agriculture')}</li>
              </ul>
            </div>
          </div>
          <a href={t('about.partner.utas.link', 'https://www.utas.uz/')}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.universityBtn}>
            {t('about.partner.moreDetails', 'More details')}
          </a>
        </div>
      </section>

      {/* Objectives Section */}
      <section className={styles.objectivesSection}>
        <h2 className={styles.sectionTitle}>{t('about.objectives.title', 'Objectives of the Partnership')}</h2>
        <div className={styles.objectivesGrid}>
          <div className={styles.objectiveItem}>
            <div className={styles.objectiveNumber}>01</div>
            <p>{t('about.objectives.obj1', 'Publishing high-quality scientific research in the field of applied engineering sciences.')}</p>
          </div>
          <div className={styles.objectiveItem}>
            <div className={styles.objectiveNumber}>02</div>
            <p>{t('about.objectives.obj2', 'Encouraging international scientific cooperation and cross-border academic exchange.')}</p>
          </div>
          <div className={styles.objectiveItem}>
            <div className={styles.objectiveNumber}>03</div>
            <p>{t('about.objectives.obj3', 'Creating a platform for knowledge and experience sharing among scientists from various countries.')}</p>
          </div>
          <div className={styles.objectiveItem}>
            <div className={styles.objectiveNumber}>04</div>
            <p>{t('about.objectives.obj4', 'Enhancing accessibility to scientific research for a global audience.')}</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;